import { useState } from 'react';

import { productionApi } from 'api/production';
import ColorInput from 'components/storefront/StorefrontConfigEditor/components/ColorInput';
import ImageInput from 'components/storefront/StorefrontConfigEditor/components/ImageInput';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { CONTENT_LINK_URL_PATTERN, CONTENT_LINK_URL_TITLE } from 'shared/utils/contentLinkUrl';

const DISPLAY_NAME = 'Banner 1';

/**
 * Default configuration for the Banner1 slice item
 *
 * @constant {Object}
 * @property {string} bgColor - Background color in hex format
 * @property {string} text - Banner text content
 * @property {string} imageLeftUrl - Left image URL
 * @property {string} imageRightUrl - Right image URL
 * @property {string} linkUrl - Banner link URL
 * @property {boolean} openLinkInNewTab - Whether to open link in new tab
 */
const DEFAULT_BANNER_ITEM = Object.freeze({
    bgColor: '#FFFFFF',
    text: '',
    imageLeftUrl: '',
    imageRightUrl: '',
    linkUrl: '',
    openLinkInNewTab: true,
});

/**
 * Default configuration for the Banner 1 slice component
 *
 * This is a hardcoded list of length one due to how the backend validation is designed
 *
 * @constant {Object}
 * @property {string} name - Name of the banner
 * @property {Array} items - Array of banner items
 */
const DEFAULT_SLICE = Object.freeze({
    name: '',
    items: [DEFAULT_BANNER_ITEM],
});

/**
 * Banner 1 slice component
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Function} props.onChange - Callback function for handling slice changes
 * @param {Object} props.slice - Slice configuration object
 * @returns {JSX.Element} Banner1 component
 */
function Banner1({ disabled, onChange, slice }) {
    const [imageErrorLeft, setImageErrorLeft] = useState(null);
    const [imageErrorRight, setImageErrorRight] = useState(null);

    const handleChange = (e) => {
        onChange({ ...slice, items: [{ ...slice.items[0], [e.target.name]: e.target.value }] });
    };

    const handleNameChange = (e) => {
        onChange({ ...slice, name: e.target.value });
    };

    const handleImageLeftUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ ...slice, items: [{ ...slice.items[0], imageLeftUrl: imageUrl }] });
            setImageErrorLeft(null);
        } catch (e) {
            setImageErrorLeft(`Error: ${e.message}`);
        }
    };

    const handleImageRightUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ ...slice, items: [{ ...slice.items[0], imageRightUrl: imageUrl }] });
            setImageErrorRight(null);
        } catch (e) {
            setImageErrorRight(`Error: ${e.message}`);
        }
    };

    const handleImageLeftClear = () => {
        onChange({ ...slice, items: [{ ...slice.items[0], imageLeftUrl: '' }] });
    };

    const handleImageRightClear = () => {
        onChange({ ...slice, items: [{ ...slice.items[0], imageRightUrl: '' }] });
    };

    const handleCheckboxChange = (checked) => {
        onChange({ ...slice, items: [{ ...slice.items[0], openLinkInNewTab: checked }] });
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
                <Label htmlFor={`name-${slice.id}`}>Name</Label>
                <p className="text-xs text-muted-foreground -mt-0.5">
                    For internal reference only. Not displayed to customers.
                </p>
                <Input
                    type="text"
                    id={`name-${slice.id}`}
                    name="name"
                    value={slice.name ?? ''}
                    placeholder="Enter banner name"
                    onChange={handleNameChange}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`text-${slice.id}`}>Text</Label>
                <Input
                    type="text"
                    id={`text-${slice.id}`}
                    name="text"
                    value={slice.items[0].text ?? ''}
                    placeholder="Enter banner text"
                    onChange={handleChange}
                    maxLength={55}
                    required
                    disabled={disabled}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <ImageInput
                    id={`imageLeft-${slice.id}`}
                    imageUrl={slice.items[0].imageLeftUrl}
                    error={imageErrorLeft}
                    onClearImage={handleImageLeftClear}
                    onImageUpload={handleImageLeftUpload}
                    disabled={disabled}
                />
                <ImageInput
                    id={`imageRight-${slice.id}`}
                    imageUrl={slice.items[0].imageRightUrl}
                    error={imageErrorRight}
                    onClearImage={handleImageRightClear}
                    onImageUpload={handleImageRightUpload}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`linkUrl-${slice.id}`}>Link URL</Label>
                <Input
                    type="text"
                    id={`linkUrl-${slice.id}`}
                    name="linkUrl"
                    value={slice.items[0].linkUrl ?? ''}
                    placeholder="Enter link URL"
                    onChange={handleChange}
                    disabled={disabled}
                    pattern={CONTENT_LINK_URL_PATTERN}
                    title={CONTENT_LINK_URL_TITLE}
                />
            </div>

            <div className="col-span-2 flex items-center justify-end gap-2">
                <Checkbox
                    id={`openLinkInNewTab-${slice.id}`}
                    name="openLinkInNewTab"
                    checked={slice.items[0].openLinkInNewTab}
                    onCheckedChange={handleCheckboxChange}
                    className="h-5 w-5"
                    disabled={disabled}
                />
                <Label className="font-normal" htmlFor={`openLinkInNewTab-${slice.id}`}>
                    Open Link in New Tab
                </Label>
            </div>

            <ColorInput
                id={`bgColor-${slice.id}`}
                label="Background Color"
                name="bgColor"
                value={slice.items[0].bgColor}
                onChange={handleChange}
                disabled={disabled}
            />
        </div>
    );
}

const validateSlice = (slice, sliceIndex) => {
    const errors = [];

    if (!slice.items[0].imageLeftUrl) {
        errors.push(`${DISPLAY_NAME} (position ${sliceIndex}) requires a left image.`);
    }

    if (!slice.items[0].imageRightUrl) {
        errors.push(`${DISPLAY_NAME} (position ${sliceIndex}) requires a right image.`);
    }

    return errors;
};

export default Object.assign(Banner1, {
    category: 'banner',
    defaultSlice: DEFAULT_SLICE,
    description: 'A clickable banner with text and two accent images.',
    displayName: DISPLAY_NAME,
    thumbnail: 'https://img-cdn.misfitsmarket.com/test/lettuce/Minimap-Thumbnail__Banner-1__Desktop',
    displayType: 'BANNER_1',
    validateSlice,
});
